import { SubmissionStatus } from '@bp/bp-graphql-types';
import {
  Button,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  Table,
  TableColumns,
  useDefaultSorting,
} from '@bp/ui-components';
import { BpLink } from 'components/BpLink/BpLink';
import { useMatrixClient } from 'hooks/matrix/useMatrixClient';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useRequireParams } from 'hooks/useRequireParams';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { niceDate } from 'utils/dateCalculations';
import { useRooms } from '../../../matrix/hooks/useRooms';
import { Assignment, useAssignments } from '../hooks/useAssignments';

type AssignmentsTeacherTableProps = {
  type: 'future' | 'past';
  loading: boolean;
  onNavigate: (uuid: string) => void;
  onEdit: (uuid: string) => void;
  onDelete: (uuid: string) => void;
};

export const AssignmentsTeacherTable = ({
  type,
  loading,
  onNavigate,
  onEdit,
  onDelete,
}: AssignmentsTeacherTableProps) => {
  const { t } = useTranslation();
  const pimAuthClaims = useAuthClaims().pimAuthClaims;
  const { courseUuid } = useRequireParams(['courseUuid']);

  const { futureAssignments, pastAssignments } = useAssignments({ courseUuid });
  const assignmentsToShow = type === 'future' ? futureAssignments : pastAssignments;

  const permissions = usePermissionChecker();

  const { isAvailable: online } = useMatrixClient();
  const rooms = useRooms();

  const { sorting, onSortingChange } = useDefaultSorting();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <needs to check 'online' state>
  const columns: TableColumns<Assignment>[] = useMemo(() => {
    return [
      {
        id: 'title',
        accessorKey: 'title',
        header: t('common.name'),
        size: 250,
        canExpand: true,
        cell: ({ row }) => {
          let newMessages = false;

          row.original.submissions.forEach((submission) => {
            const room = rooms?.find((room) => {
              return room.name.includes(submission.uuid);
            });
            if (room && !newMessages) {
              newMessages = room.getRoomUnreadNotificationCount() > 0;
            }
          });

          return (
            <BpLink
              hasNews={newMessages}
              value={row.original.title}
              onNavigate={() => onNavigate(row.original.uuid)}
              counter={row.original.submissions.filter((s) => s.status === SubmissionStatus.New).length}
              allowAfterDue={row.original.allowSubmissionAfterDueDate ?? false}
              isEditorOfCourse
            />
          );
        },
      },
      {
        id: 'teachingUnitTitle',
        accessorKey: 'teachingUnitTitle',
        header: t('teachingUnits.titleSingular'),
        size: 250,
        cell: ({ row }) => row.original.teachingUnit.title,
      },
      {
        id: 'visibleFrom',
        accessorKey: 'visibleFrom',
        header: t('common.activeFrom'),
        size: 150,
        cell: ({ row }) => niceDate(row.original.visibleFrom, 'medium'),
      },
      {
        id: 'dueDate',
        accessorKey: 'dueDate',
        header: t('assignments.dueAt'),
        size: 150,
        cell: ({ row }) => niceDate(row.original.dueDate, 'medium'),
      },
      {
        id: 'created',
        accessorKey: 'created',
        header: t('common.created'),
        size: 150,
        cell: ({ row }) => niceDate(row.original.createdAt, 'medium'),
      },
    ];
  }, [onNavigate, rooms, online, t]);

  function getMenu(uuid: string): ReactNode {
    const canUpdate = permissions?.canUpdateAssignment({
      uuid: uuid,
      ownerUuid: pimAuthClaims.getProfile().uuid,
      groupUuid: courseUuid ?? '',
      organization: { uuid: pimAuthClaims.getOrganizationUuid() },
    });

    return (
      <Dropdown trigger={<Button hierarchy='ghost' icon={<DotsVerticalIcon className='small' />} />}>
        <DropdownMenu
          data={[
            {
              label: t('common.edit'),
              onClick: () => onEdit(uuid),
              disabled: !canUpdate,
            },

            {
              type: 'ruler',
            },
            {
              color: 'error',
              label: t('delete.delete'),
              onClick: () => onDelete(uuid),
              disabled: !canUpdate,
            },
          ]}
        />
      </Dropdown>
    );
  }

  return (
    <Table<Assignment>
      loading={loading}
      columns={columns}
      data={assignmentsToShow}
      showActionBar={assignmentsToShow.length > 0}
      actionBarSettings={{
        marginBottom: 'var(--spacing-3)',
      }}
      showSort
      sorting={sorting}
      onSortingChange={onSortingChange}
      hideHeader={assignmentsToShow.length === 0}
      customPadding='var(--spacing-6)'
      lastColWidth='var(--spacing-4)'
      lastCol={({ original }) => getMenu(original.uuid)}
      emptyStateSettings={{
        size: 'small',
        subtitle: type === 'future' ? t('assignments.noFuture') : t('assignments.noPast'),
        title: '',
        hideIcon: true,
        padding: 'l',
      }}
    />
  );
};
